class StoreLocator {
  constructor() {
    this.init();
  }

  init() {
    this.setupLazyLoading();
    this.setupPhoneLinks();
    this.setupScrollableStores();
    this.setupMapInteraction();
    this.setupAccessibility();
    this.setupResponsiveFeatures();
  }

  // Lazy loading animation for store cards
  setupLazyLoading() {
    const cards = document.querySelectorAll('.store-locator__store-card, .store-locator__enhanced-card');
    
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('store-locator__loading');
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px',
        threshold: 0.1
      });

      cards.forEach(card => observer.observe(card));
    } else {
      // Fallback for browsers without IntersectionObserver
      cards.forEach(card => card.classList.add('store-locator__loading'));
    }
  }

  // Enhanced phone link functionality
  setupPhoneLinks() {
    const phoneLinks = document.querySelectorAll('.store-locator__store-phone, .store-locator__enhanced-phone');
    
    phoneLinks.forEach(link => {
      // Add click tracking
      link.addEventListener('click', (e) => {
        this.trackPhoneClick(link.textContent);
      });

      // Add hover effects
      link.addEventListener('mouseenter', () => {
        this.showPhoneTooltip(link);
      });

      link.addEventListener('mouseleave', () => {
        this.hidePhoneTooltip();
      });
    });
  }

  // Setup smooth scrolling for the stores container
  setupScrollableStores() {
    const scrollContainer = document.querySelector('.store-locator__stores-scrollable');
    if (!scrollContainer) return;

    // Add smooth scrolling behavior
    scrollContainer.style.scrollBehavior = 'smooth';

    // Add scroll indicators if content overflows
    this.addScrollIndicators(scrollContainer);

    // Handle scroll events for fade effects
    scrollContainer.addEventListener('scroll', () => {
      this.handleScrollFade(scrollContainer);
    });
  }

  addScrollIndicators(container) {
    if (window.innerWidth < 1200) return; // Desktop only

    const isScrollable = container.scrollHeight > container.clientHeight;
    if (!isScrollable) return;

    // Create scroll indicator
    const indicator = document.createElement('div');
    indicator.className = 'store-locator__scroll-indicator';
    indicator.innerHTML = '↓ Scroll for more stores';
    indicator.style.cssText = `
      position: absolute;
      bottom: 10px;
      right: 10px;
      background: rgba(52, 152, 219, 0.9);
      color: white;
      padding: 0.5rem;
      border-radius: 4px;
      font-size: 0.75rem;
      pointer-events: none;
      opacity: 0.8;
      z-index: 10;
    `;

    container.style.position = 'relative';
    container.appendChild(indicator);

    // Hide indicator when scrolled to bottom
    container.addEventListener('scroll', () => {
      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
      indicator.style.opacity = isAtBottom ? '0' : '0.8';
    });
  }

  handleScrollFade(container) {
    const cards = container.querySelectorAll('.store-locator__enhanced-card');
    const containerRect = container.getBoundingClientRect();
    
    cards.forEach(card => {
      const cardRect = card.getBoundingClientRect();
      const cardTop = cardRect.top - containerRect.top;
      const cardBottom = cardRect.bottom - containerRect.top;
      
      // Calculate fade based on position
      let opacity = 1;
      if (cardTop < 0) {
        opacity = Math.max(0.3, 1 + cardTop / 100);
      } else if (cardBottom > containerRect.height) {
        const overflow = cardBottom - containerRect.height;
        opacity = Math.max(0.3, 1 - overflow / 100);
      }
      
      card.style.opacity = opacity;
    });
  }

  // Map interaction enhancements
  setupMapInteraction() {
    const mapContainer = document.querySelector('.store-locator__map');
    if (!mapContainer) return;

    // Prevent scroll hijacking on mobile
    let isScrolling = false;
    
    mapContainer.addEventListener('touchstart', () => {
      isScrolling = false;
    });

    mapContainer.addEventListener('touchmove', () => {
      isScrolling = true;
    });

    mapContainer.addEventListener('touchend', (e) => {
      if (isScrolling) {
        e.preventDefault();
      }
    });

    // Add click-to-focus behavior
    mapContainer.addEventListener('click', () => {
      mapContainer.style.pointerEvents = 'auto';
      setTimeout(() => {
        mapContainer.style.pointerEvents = 'none';
      }, 2000);
    });
  }

  // Accessibility improvements
  setupAccessibility() {
    const storeCards = document.querySelectorAll('.store-locator__store-card, .store-locator__enhanced-card');
    
    storeCards.forEach((card, index) => {
      // Add ARIA labels
      card.setAttribute('role', 'article');
      card.setAttribute('aria-label', `Store location ${index + 1}`);
      
      // Add keyboard navigation
      card.setAttribute('tabindex', '0');
      
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const phoneLink = card.querySelector('.store-locator__store-phone, .store-locator__enhanced-phone');
          if (phoneLink) {
            phoneLink.click();
          }
        }
      });
    });
  }

  // Responsive features
  setupResponsiveFeatures() {
    this.handleResize();
    window.addEventListener('resize', this.debounce(() => {
      this.handleResize();
    }, 250));
  }

  handleResize() {
    const container = document.querySelector('.store-locator__container');
    if (!container) return;

    const isMobile = window.innerWidth < 990;
    
    // Adjust map height on orientation change
    if (window.orientation !== undefined) {
      const map = document.querySelector('.store-locator__map');
      if (map && isMobile) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }
    }

    // Optimize for very small screens
    if (window.innerWidth < 400) {
      container.style.padding = '0 0.5rem';
    } else {
      container.style.padding = '';
    }
  }

  // Utility functions
  trackPhoneClick(phoneNumber) {
    // Analytics tracking for phone clicks
    if (typeof gtag !== 'undefined') {
      gtag('event', 'phone_click', {
        'event_category': 'store_locator',
        'event_label': phoneNumber
      });
    }
    
    console.log('Phone clicked:', phoneNumber);
  }

  showPhoneTooltip(element) {
    const tooltip = document.createElement('div');
    tooltip.className = 'store-locator__phone-tooltip';
    tooltip.textContent = 'Click to call';
    tooltip.style.cssText = `
      position: absolute;
      background: #2c3e50;
      color: white;
      padding: 0.5rem;
      border-radius: 4px;
      font-size: 0.75rem;
      z-index: 1000;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s;
    `;
    
    document.body.appendChild(tooltip);
    
    const rect = element.getBoundingClientRect();
    tooltip.style.left = rect.left + 'px';
    tooltip.style.top = (rect.bottom + 5) + 'px';
    
    requestAnimationFrame(() => {
      tooltip.style.opacity = '1';
    });
    
    this.currentTooltip = tooltip;
  }

  hidePhoneTooltip() {
    if (this.currentTooltip) {
      this.currentTooltip.style.opacity = '0';
      setTimeout(() => {
        if (this.currentTooltip && this.currentTooltip.parentNode) {
          this.currentTooltip.parentNode.removeChild(this.currentTooltip);
        }
        this.currentTooltip = null;
      }, 200);
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Public API methods
  addStore(storeData) {
    const storesContainer = document.querySelector('.store-locator__stores');
    if (!storesContainer) return;

    const storeCard = this.createStoreCard(storeData);
    storesContainer.appendChild(storeCard);
    
    // Re-initialize for new card
    this.setupLazyLoading();
    this.setupPhoneLinks();
    this.setupAccessibility();
  }

  createStoreCard(data) {
    const card = document.createElement('div');
    card.className = 'store-locator__store-card store-locator__enhanced-card';
    
    card.innerHTML = `
      ${data.name ? `<h3 class="store-locator__store-name store-locator__enhanced-name">${data.name}</h3>` : ''}
      ${data.address ? `<div class="store-locator__store-address store-locator__enhanced-address">${data.address}</div>` : ''}
      ${data.phone ? `<a href="tel:${data.phone.replace(/\s|-/g, '')}" class="store-locator__store-phone store-locator__enhanced-phone">Phone Number: ${data.phone}</a>` : ''}
      ${data.hours ? `<div class="store-locator__enhanced-hours">${data.hours}</div>` : ''}
      ${data.info ? `<div class="store-locator__enhanced-info">${data.info}</div>` : ''}
    `;
    
    return card;
  }

  updateMap(embedCode) {
    const mapContainer = document.querySelector('.store-locator__map');
    if (mapContainer) {
      mapContainer.innerHTML = embedCode;
      this.setupMapInteraction();
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StoreLocator();
  });
} else {
  new StoreLocator();
}

// Export for potential external use
window.StoreLocator = StoreLocator;